import { browserTransferOptions, clientV3SDK, getBrowserHasher } from "@istari-frontend/api-client";
import { type ContentToken, type FileRevision, TransferFailedError, type V3Models } from "@istari/istari-client";

import { type UploadContext, UploadFileMismatchError } from "./upload-payloads";

type Metadata = {
  displayName?: string;
  description?: string;
  versionName?: string;
  externalIdentifier?: string;
};

export async function createUploadRevision(file: File, context: UploadContext, metadata: Metadata, modelId?: string) {
  let token: ContentToken | undefined;
  let contentComplete = false;
  try {
    const revision = await clientV3SDK.createRevision(
      { file, ...metadata, modelId },
      {
        transfer: browserTransferOptions,
        signal: context.signal,
        salt: context.salt,
        modelId,
        hasher: async (blob, salt, options) => {
          token = await getBrowserHasher()(blob, salt, options);
          if (context.token && (token.sha !== context.token.sha || token.salt !== context.token.salt)) {
            throw new UploadFileMismatchError("Choose the original file to resume.");
          }
          return token;
        },
        onProgress: (event) => {
          if (event.kind === "complete" && event.phase === "transfer") contentComplete = true;
          context.onProgress(event);
        },
      },
    );
    return revision;
  } catch (error) {
    if (
      error instanceof TransferFailedError &&
      error.token &&
      token &&
      !contentComplete &&
      !context.signal.aborted &&
      error.status !== 401 &&
      error.status !== 403 &&
      error.token.sha === token.sha &&
      error.token.salt === token.salt
    ) {
      context.captureToken(token);
    }
    throw error;
  }
}

export function revisionMetadata(revision: FileRevision) {
  return {
    name: revision.name ?? "",
    extension: revision.extension ?? null,
    size: revision.size ?? null,
    mime: revision.mime ?? null,
    description: revision.description ?? null,
    versionName: revision.versionName ?? null,
    externalIdentifier: revision.externalIdentifier ?? null,
    displayName: revision.displayName ?? null,
    contentToken: { sha: revision.contentToken.sha, salt: revision.contentToken.salt },
    propertiesToken: { sha: revision.propertiesToken.sha, salt: revision.propertiesToken.salt },
  };
}

export async function commitResource(
  revision: FileRevision,
  resourceType: V3Models.ResourceCreateTypeDto,
  context: UploadContext,
) {
  context.metadata();
  return clientV3SDK.createResource(
    { resourceCreateDto: { ...revisionMetadata(revision), resourceType, upstreamRemoteInfo: null } },
    { signal: context.signal },
  );
}
